import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios"
import { formDataPlugin, zodValidationPlugin, ZodiosError } from "@zodios/core"
import type { ZodiosPlugin } from "@zodios/core"
import axios from "axios"
import type { DeepReadonlyObject } from "@zodios/core/lib/utils.types"
import { unref } from "vue"
import * as dt from "date-fns"
import { createApiClient, endpoints } from "~/composables/codegen/api-client"
import { stringify } from "qs"

// TODO: will be removed once new client only (no need for a composable)
// oxlint-disable-next-line import/no-mutable-exports
export let axiosInstance: AxiosInstance | undefined

type GetAccesTokenFn = (..._: any[]) => Promise<string>

// The backend error body shape varies: plain string, `{message}` object, or a
// FastAPI `{detail}` envelope (detail itself may be a string or an object).
// Exported for unit testing only.
export function extractBackendMessage(data: unknown): string | undefined {
  if (R.isString(data)) return data
  if (R.isPlainObject(data)) {
    const msg = (data as { message?: unknown }).message ?? (data as { detail?: unknown }).detail
    if (R.isString(msg)) return msg
  }
  return undefined
}

declare module "axios" {
  interface AxiosRequestConfig {
    // false when the caller shows its own error notification
    errorNotification?: boolean
  }
}

// A 404 is expected when optional data is missing and is handled by the requesting UI.
export function shouldDisplayErrorNotification(statusCode: unknown): statusCode is number {
  return R.isNumber(statusCode) && statusCode !== 404
}

// Only use this for schemas without async refinements. Keep validation and date coercion, without
// allocating promises for every field of a large response.
export function synchronousResponseValidationPlugin(): ZodiosPlugin {
  return {
    ...zodValidationPlugin({
      validate: "request",
      transform: "request",
      sendDefaults: false,
    }),
    response: async (api, config, response) => {
      const contentType = response.headers["content-type"]
      const isJsonResponse =
        R.isString(contentType) &&
        (contentType.includes("application/json") ||
          contentType.includes("application/vnd.api+json"))

      if (!isJsonResponse) return response

      const endpoint = api.find((e) => e.method === config.method && e.path === config.url)
      if (!endpoint) throw new Error(`No endpoint found for ${config.method} ${config.url}`)

      const result = endpoint.response.safeParse(response.data)
      if (!result.success) {
        throw new ZodiosError(
          `Zodios: Invalid response from endpoint '${config.method} ${config.url}'`,
          // ZodiosError bundles older Axios config typings than the response plugin.
          config as ConstructorParameters<typeof ZodiosError>[1],
          response.data,
          result.error,
        )
      }

      response.data = result.data
      return response
    },
  }
}

function getAxiosInstance(
  baseUrl: string,
  access_token_fn: GetAccesTokenFn | undefined,
): AxiosInstance {
  interface RequestConfig extends AxiosRequestConfig {
    startTime?: Date
  }

  // @ts-expect-error wrong extend on generic
  interface Response extends AxiosResponse {
    config: RequestConfig
  }

  const config: RequestConfig = {
    baseURL: baseUrl,
    responseType: "json",
    headers: { "Content-Type": "application/json" },
    paramsSerializer: (params) =>
      stringify(params, {
        arrayFormat: "repeat",
        serializeDate: (date: Date) => dt.formatISO(date),
      }),
  }

  const instance = axios.create(config)
  axiosInstance = instance // TODO: remove once legacy client is no more

  // @ts-expect-error to fix
  instance.interceptors.request.use((request: RequestConfig) => {
    request.startTime = new Date()
    if (import.meta.dev) {
      console.log("Requesting ", request.url, " ...")
    }

    // console.log("Starting Request", JSON.stringify(request, null, 2));
    return request
  })

  function toLogMessage(response: Response): string {
    let msg = [response.status, response.config.method?.toUpperCase(), response.config.url].join(
      " - ",
    )

    const endTime = new Date()
    const startTime = response.config.startTime
    if (startTime) {
      const duration = endTime.getTime() - startTime.getTime()
      msg = `${msg} took ${duration} ms`
    }
    return msg
  }

  instance.interceptors.response.use(
    (response) => {
      if (import.meta.dev) {
        console.log(toLogMessage(response))
      }

      // 204 No Content: axios surfaces an empty string body, but endpoints declare
      // `response: z.void()`. Normalize so zodios validation passes.
      if (response.status === 204 && response.data === "") {
        response.data = undefined
      }

      return response
    },
    (error) => {
      // https://www.intricatecloud.io/2020/03/how-to-handle-api-errors-in-your-web-app-using-axios/
      if (import.meta.dev) {
        if (error.response) {
          // client received an error response (5xx, 4xx)
          console.error(toLogMessage(error.response))
          console.dir(error.response)
        } else if (error.request) {
          // client never received a response, or request never left
          // `error.request` is an instance of XMLHttpRequest in the browser
          console.dir(error.request)
        } else {
          // Something happened in setting up the request that triggered an Error
          console.error(error.message)
        }
      }

      const status_code = error.response?.status
      if (
        error.config?.errorNotification !== false &&
        shouldDisplayErrorNotification(status_code)
      ) {
        useNotifStore().addNotification({
          title: "Query failed",
          // BNotif.text is a required string; the store defaults empty text on display
          text: extractBackendMessage(error.response!.data) ?? "",
          // 5xx are server faults → alert; 4xx are client-facing errors (validation,
          // permissions, not-found) that the user can act on → lower severity.
          level: status_code >= 500 ? NotifLevel.enum.alert : NotifLevel.enum.warning,
        })
      }

      return Promise.reject(error)
    },
  )

  if (access_token_fn) {
    instance.interceptors.request.use(async (request) => {
      const access_token = await access_token_fn()
      request.headers!.Authorization = `Bearer ${access_token}`
      return request
    })
  }

  return instance
}

export function useAxiosInstance(): AxiosInstance {
  if (!axiosInstance) {
    throw new Error("Axios instance not yet instanciated")
  } else {
    return axiosInstance
  }
}

export type ApiClientT = ReturnType<typeof createApiClient>

let client: ApiClientT | undefined

export function useApiClient(access_token_fn: GetAccesTokenFn | undefined = undefined) {
  if (client) {
    return client
  }

  const baseUrl = useRuntimeConfig().public.apiBaseUrl

  client = createApiClient(baseUrl, {
    validate: false,
    transform: false,
    sendDefaults: false,
    axiosInstance: getAxiosInstance(baseUrl, access_token_fn),
  })

  // Let's disable zod validation for specific endpoints.
  const zodiosPlugWithoutValidate = zodValidationPlugin({
    validate: "request",
    transform: "request",
    sendDefaults: false,
  })

  // For response being z.void(), fastapi still return an empty string, and it makes the zod validation fail.
  const void_response_aliases = ["delete_document", "delete_events", "delete_site_status"] as const

  // For responses being slow to parse (array of unions for example)
  const slow_zod_parse_aliases = [
    "get_goldensource_kpis",
    "get_sites_with_datahealth_filtered_by_model",
    "get_datahealth",
  ] as const

  const zod_disabled_aliases = [...void_response_aliases, ...slow_zod_parse_aliases]
  zod_disabled_aliases.forEach((alias) => client!.use(alias, zodiosPlugWithoutValidate))

  const zodiosPlugWithValidate = zodValidationPlugin({
    validate: true,
    transform: true,
    sendDefaults: false,
  })
  const other_aliases = endpoints
    .map((e) => e.alias)
    .filter((alias) => !zod_disabled_aliases.includes(alias as any))

  other_aliases.forEach((alias) => client!.use(alias, zodiosPlugWithValidate))

  client.use("get_sites_filtered_by_model", synchronousResponseValidationPlugin())
  client.use("get_contracts_filtered_by_model", synchronousResponseValidationPlugin())
  client.use("get_invoices_filtered_by_model", synchronousResponseValidationPlugin())
  client.use("get_events_filtered_by_model", synchronousResponseValidationPlugin())
  client.use("get_notes_filtered_by_model", synchronousResponseValidationPlugin())

  // update_invoice's `extra` body is a union of two all-optional object
  // schemas: zod resolves a load-profile payload against the first member and
  // strips the prod/conso energies from the request. Skip request parsing for
  // this endpoint (response validation kept); queries.ts sends the body as-is.
  client!.use(
    "update_invoice",
    zodValidationPlugin({ validate: "response", transform: "response", sendDefaults: false }),
  )

  // Custom handle for file upload endpoint (to avoid having wrong Content-Type header)
  // https://github.com/ecyrbe/zodios/issues/408
  client!.use("create_document", {
    ...zodiosPlugWithValidate,
    request: async (_api, config) => {
      return {
        ...config,
        headers: {
          ...config.headers,
          "Content-Type": "multipart/form-data",
        },
      }
    },
  })

  function toFormData(obj: Record<string, any>): FormData {
    function appendForKey(key: string, value: any, formData: FormData): void {
      if (R.isArray(value)) {
        value.forEach((e: any) => appendForKey(e.name, e, formData))
      } else if ((value as any) instanceof File) {
        formData.append(key, value)
      } else if (R.isString(value)) {
        formData.append(key, value)
      } else if (R.isBoolean(value)) {
        formData.append(key, String(value))
      } else {
        throw new TypeError(`Conversion for ${key} is not implemented`)
      }
    }

    const formData = new FormData()
    for (const key in obj) {
      appendForKey(key, obj[key], formData)
    }
    return formData
  }

  client!.use("create_document", {
    name: formDataPlugin().name,
    request: async (_api, config) => {
      if (!R.isPlainObject(config.data)) {
        throw new TypeError("Zodios: multipart/form-data body must be an object")
      }
      const data = toFormData(config.data)
      return { ...config, data }
    },
  })

  return client
}

type EndpointAliases = (typeof endpoints)[number]["alias"]

type ApiEndpointConfigOpts<Key extends EndpointAliases> = ApiClientT[Key] extends (
  ...args: [DeepReadonlyObject<infer B>, DeepReadonlyObject<infer C>]
) => Promise<any>
  ? B extends { httpAgent?: any }
    ? B
    : C
  : never

type ApiEndpointBody<Key extends EndpointAliases> = ApiClientT[Key] extends (
  ...args: [DeepReadonlyObject<infer B>, DeepReadonlyObject<any>]
) => Promise<any>
  ? B extends { httpAgent?: any }
    ? never
    : B
  : never

// https://www.zodios.org/docs/client#zodiosalias
export function dispatchEndpointArgs<Key extends EndpointAliases>(
  opts: MaybeRef<Record<string, any>>,
  alias: Key,
): { body: ApiEndpointBody<Key>; config: ApiEndpointConfigOpts<Key> } {
  const endpoint = endpoints.find((e) => e.alias === alias)
  if (!endpoint) {
    throw new Error(`Could not find endpoint named: ${alias}`)
  }

  const config: Record<"params" | "queries", any> = { params: {}, queries: {} }
  let body: Record<string, any> = {}

  const parameters = "parameters" in endpoint ? endpoint.parameters : []
  const sainOpts = unref(opts)

  parameters.forEach((item) => {
    if (item.name in sainOpts) {
      if (item.type === "Path") {
        config.params[item.name] = sainOpts[item.name]
      } else if (item.type === "Query") {
        config.queries[item.name] = sainOpts[item.name]
      }
    }

    if (item.name === "body") {
      body = item.schema.parse(sainOpts)
    }
  })

  return { body, config } as any
}

type _BaseConfigT = { params?: any; queries?: any } | undefined

type FlattenApiOpts<T extends _BaseConfigT> = T extends {
  params?: DeepReadonlyObject<infer P>
  queries?: DeepReadonlyObject<infer Q>
}
  ? (P extends NonNullable<unknown> ? P : {}) & (Q extends NonNullable<unknown> ? Q : {})
  : never

export type InferGetInput<Key extends EndpointAliases> = Prettify<
  ApiEndpointConfigOpts<Key> extends _BaseConfigT
    ? FlattenApiOpts<ApiEndpointConfigOpts<Key>>
    : never
>

export type InferMutateInput<Key extends EndpointAliases> = Prettify<
  ApiEndpointConfigOpts<Key> extends _BaseConfigT
    ? FlattenApiOpts<ApiEndpointConfigOpts<Key>> & ApiEndpointBody<Key>
    : ApiEndpointBody<Key>
>

// type XGET = ApiClientT["get_goldensource_timeseries"]
// type XGETCFG = ApiEndpointConfigOpts<"get_goldensource_timeseries">
// type XGETBODY = ApiEndpointBody<"get_goldensource_timeseries">
// type XGETFLATRAW = FlattenApiOpts<ApiEndpointConfigOpts<"get_goldensource_timeseries">>
// type XGETFLAT = InferGetInput<"get_goldensource_timeseries">

// type XPOST = ApiClientT["create_event"]
// type XPOSTCFG = ApiEndpointConfigOpts<"create_event">
// type XPOSTBODY = ApiEndpointBody<"create_event">
// type XPOSTFLAT = InferGetInput<"create_event">
// type XPOSTFLATMUT = InferMutateInput<"create_event">
